import React, { useCallback, useEffect } from 'react';
import { CardViewContentProps } from './types';
import { Box, Typography } from '@mui/material';
import LibraryCategoryCard from '../../../../common/library-category-card/LibraryCategoryCard';
import ApartmentOutlinedIcon from '../../../../../assets/svg/apartment.svg?react';
import BadgeOutlinedIcon from '../../../../../assets/svg/badge-light.svg?react';
import SupportAgentOutlinedIcon from '../../../../../assets/svg/support-agent.svg?react';
import BusinessCenterIcon from '../../../../../assets/svg/business-center-v2.svg?react';
import { useTranslation } from 'react-i18next';
import LibraryList from '../library-list/LibraryList';
import styles from '../../scss/Library.module.scss';
import { BusinessCategoryContent } from './BusinessCategoryContent';
import { EmploymentCategoryContent } from './EmploymentCategoryContent';
import { CustomersCategoryContent } from './CustomersCategoryContent';
import { SuppliersCategoryContent } from './SuppliersCategoryContent';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PreviewBusiness from '../card-previews/preview-business/Preview';
import PreviewEmployment from '../card-previews/preview-employment/Preview';
import PreviewCustomers from '../card-previews/preview-customers/Preview';
import PreviewSuppliers from '../card-previews/preview-suppliers/Preview';

export const CardViewContent = ({
  activeTab,
  changeTabFunction,
  templates,
  workflow,
  setWorkflow,
}: CardViewContentProps) => {
  const { t } = useTranslation();
  const [searchParam] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const workflowItem = searchParam.get('workflow');
  const categoryItem = searchParam.get('category');

  const handleCategoryChange = useCallback(() => {
    if (categoryItem) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      changeTabFunction(decodeURI(categoryItem));
      if (!workflowItem) {
        navigate(location.pathname);
      }
    }
  }, [categoryItem, workflowItem, changeTabFunction, navigate, location.pathname]);

  useEffect(() => {
    handleCategoryChange();
  }, [handleCategoryChange]);

  if (activeTab === 'All') {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: '24px',
            backgroundColor: 'transparent',
            flexWrap: 'wrap',
          }}
        >
          <LibraryCategoryCard
            title={t('Business')}
            onClick={() => changeTabFunction('Business')}
            icon={<ApartmentOutlinedIcon />}
            previewBlock={<PreviewBusiness />}
          />
          <LibraryCategoryCard
            title={t('Employment')}
            onClick={() => changeTabFunction('Employment')}
            icon={<BadgeOutlinedIcon />}
            previewBlock={<PreviewEmployment />}
          />
          <LibraryCategoryCard
            title={t('Customers')}
            onClick={() => changeTabFunction('Customers')}
            icon={<SupportAgentOutlinedIcon />}
            previewBlock={<PreviewCustomers />}
          />
          <LibraryCategoryCard
            title={t('Suppliers')}
            onClick={() => changeTabFunction('Suppliers')}
            icon={<BusinessCenterIcon />}
            previewBlock={<PreviewSuppliers />}
          />
        </Box>
      </>
    );
  }
  if (!workflow) {
    if (activeTab === 'Business') {
      return <BusinessCategoryContent templates={templates} setWorkflow={setWorkflow} />;
    } else if (activeTab === 'Employment') {
      return <EmploymentCategoryContent templates={templates} setWorkflow={setWorkflow} />;
    } else if (activeTab === 'Customers') {
      return <CustomersCategoryContent templates={templates} setWorkflow={setWorkflow} />;
    } else if (activeTab === 'Suppliers') {
      return <SuppliersCategoryContent templates={templates} setWorkflow={setWorkflow} />;
    }
  } else {
    return (
      <>
        <Box className={styles.breadcrumbs}>
          <Typography
            variant='h3'
            component='h3'
            className={styles.documentViewText}
            onClick={() => {
              setWorkflow(null);
              navigate(location.pathname);
            }}
          >
            {t('headers.workflows')}
          </Typography>
          <Typography variant='body2' className={styles.separator}>
            ›
          </Typography>
          <Typography variant='body2' className={styles.documentViewText}>
            {workflow}
          </Typography>
        </Box>
        <LibraryList
          cardView={true}
          libraryItems={[...templates]
            .filter(
              (el) =>
                'category' in el &&
                el.category === activeTab &&
                'dl_workflows' in el &&
                el.dl_workflows.includes(workflow)
            )
            .sort((a, b) => a.name.localeCompare(b.name))}
        />
      </>
    );
  }
};
